import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Plus,
  Pencil,
  Trash,
  X,
  CircleNotch,
  Package,
  Star,
  Eye,
  EyeSlash,
  MagnifyingGlass,
  ArrowSquareOut,
  Check,
  Copy,
  Upload
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || '';

const CATEGORIES = [
  { id: "workwear", name: "Workwear & Clothing" },
  { id: "footwear", name: "Footwear & Boots" },
  { id: "tools", name: "Tools & Equipment" },
  { id: "tech", name: "Farm Tech & Gadgets" },
  { id: "books", name: "Books & Education" },
  { id: "animal", name: "Animal & Pet Supplies" },
  { id: "outdoor", name: "Outdoor & Country" },
  { id: "gifts", name: "Gifts & Home" },
];

const EMPTY_PRODUCT = {
  title: "",
  description: "",
  price: "",
  original_price: "",
  image_url: "",
  amazon_url: "",
  category: "workwear",
  brand: "",
  rating: "",
  review_count: "",
  featured: false,
  active: true,
  gender: "unisex",
  tags: ""
};

export default function AdminShopPage() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);
  
  // Bulk import
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkJson, setBulkJson] = useState("");
  const [importing, setImporting] = useState(false);

  // Check admin access - wait for auth to finish loading first
  useEffect(() => {
    if (authLoading) return; // Wait for auth to complete
    if (!user || user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/");
    }
  }, [user, authLoading, navigate]);

  // Fetch products
  useEffect(() => {
    fetchProducts();
    fetchStats();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/shop/admin/products`, {
        params: { include_inactive: true }
      });
      setProducts(response.data.products);
    } catch (error) {
      toast.error("Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/shop/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to load stats");
    }
  };

  const openAddModal = () => {
    setEditingProduct(null);
    setFormData(EMPTY_PRODUCT);
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      ...product,
      tags: Array.isArray(product.tags) ? product.tags.join(", ") : "",
      price: product.price?.toString() || "",
      original_price: product.original_price?.toString() || "",
      rating: product.rating?.toString() || "",
      review_count: product.review_count?.toString() || ""
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        rating: formData.rating ? parseFloat(formData.rating) : null,
        review_count: formData.review_count ? parseInt(formData.review_count) : null,
        tags: formData.tags ? formData.tags.split(",").map(t => t.trim().toLowerCase()) : []
      };

      if (editingProduct) {
        await axios.put(`${API}/api/shop/admin/products/${editingProduct.id}`, payload);
        toast.success("Product updated!");
      } else {
        await axios.post(`${API}/api/shop/admin/products`, payload);
        toast.success("Product added!");
      }

      setShowModal(false);
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await axios.delete(`${API}/api/shop/admin/products/${productId}`);
      toast.success("Product deleted");
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const toggleActive = async (product) => {
    try {
      await axios.put(`${API}/api/shop/admin/products/${product.id}`, {
        active: !product.active
      });
      toast.success(product.active ? "Product hidden" : "Product visible");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const toggleFeatured = async (product) => {
    try {
      await axios.put(`${API}/api/shop/admin/products/${product.id}`, {
        featured: !product.featured
      });
      toast.success(product.featured ? "Removed from featured" : "Added to featured");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to update product");
    }
  };

  const handleBulkImport = async () => {
    setImporting(true);
    try {
      const products = JSON.parse(bulkJson);
      const response = await axios.post(`${API}/api/shop/admin/products/bulk`, products);
      toast.success(`Imported ${response.data.created} products!`);
      setShowBulkImport(false);
      setBulkJson("");
      fetchProducts();
      fetchStats();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error("Invalid JSON format");
      } else {
        toast.error(error.response?.data?.detail || "Failed to import products");
      }
    } finally {
      setImporting(false);
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         p.brand?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || p.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Show loading state while auth is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FFF1E5] flex items-center justify-center">
        <CircleNotch size={48} className="animate-spin text-[#0F766E]" />
      </div>
    );
  }

  if (!user || user.role !== "admin") {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <Header />

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl text-[#33302E]">Shop Admin</h1>
            <p className="text-[#6B6661]">Manage your Amazon affiliate products</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowBulkImport(true)}
              className="px-4 py-2 border border-[#0F766E] text-[#0F766E] hover:bg-[#F0FDF4] flex items-center gap-2"
            >
              <Upload size={18} />
              Bulk Import
            </button>
            <button
              onClick={openAddModal}
              className="px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] flex items-center gap-2"
            >
              <Plus size={18} />
              Add Product
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white border border-[#E0D4C8] p-4">
              <p className="text-xs text-[#6B6661] uppercase">Total Products</p>
              <p className="text-2xl font-mono text-[#33302E]">{stats.total_products}</p>
            </div>
            <div className="bg-white border border-[#E0D4C8] p-4">
              <p className="text-xs text-[#6B6661] uppercase">Total Clicks</p>
              <p className="text-2xl font-mono text-[#0F766E]">{stats.total_clicks}</p>
            </div>
            <div className="bg-white border border-[#E0D4C8] p-4">
              <p className="text-xs text-[#6B6661] uppercase">Categories</p>
              <p className="text-2xl font-mono text-[#33302E]">{Object.keys(stats.categories).length}</p>
            </div>
            <div className="bg-white border border-[#E0D4C8] p-4">
              <p className="text-xs text-[#6B6661] uppercase">Featured</p>
              <p className="text-2xl font-mono text-[#F59E0B]">{products.filter(p => p.featured).length}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6661]" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-[#E0D4C8] bg-white focus:outline-none focus:border-[#0F766E]"
            />
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-4 py-2 border border-[#E0D4C8] bg-white focus:outline-none focus:border-[#0F766E]"
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Products Table */}
        <div className="bg-white border border-[#E0D4C8] overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <CircleNotch size={32} className="animate-spin text-[#0F766E] mx-auto" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="p-12 text-center">
              <Package size={48} className="text-[#D1D5DB] mx-auto mb-4" />
              <p className="text-[#6B6661]">No products found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F9FAFB] border-b border-[#E0D4C8]">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6661] uppercase">Product</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-[#6B6661] uppercase">Category</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-[#6B6661] uppercase">Price</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-[#6B6661] uppercase">Clicks</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-[#6B6661] uppercase">Status</th>
                    <th className="text-right px-4 py-3 text-xs font-medium text-[#6B6661] uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className={`hover:bg-[#F9FAFB] ${!product.active ? 'opacity-50' : ''}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img 
                            src={product.image_url} 
                            alt={product.title}
                            className="w-12 h-12 object-cover bg-[#F6E9DD]"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/48'}
                          />
                          <div>
                            <p className="text-sm font-medium text-[#33302E] line-clamp-1">{product.title}</p>
                            <p className="text-xs text-[#6B6661]">{product.brand}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-1 bg-[#F6E9DD] text-[#6B6661]">
                          {product.category}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-mono text-sm">€{product.price?.toFixed(2)}</span>
                        {product.original_price && (
                          <span className="text-xs text-[#8B8580] line-through ml-2">
                            €{product.original_price.toFixed(2)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className="font-mono text-sm text-[#0F766E]">{product.click_count || 0}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => toggleFeatured(product)}
                            className={`p-1 ${product.featured ? 'text-[#F59E0B]' : 'text-[#D1D5DB]'} hover:text-[#F59E0B]`}
                            title={product.featured ? "Remove featured" : "Make featured"}
                          >
                            <Star size={18} weight={product.featured ? "fill" : "regular"} />
                          </button>
                          <button
                            onClick={() => toggleActive(product)}
                            className={`p-1 ${product.active ? 'text-[#059669]' : 'text-[#DC2626]'}`}
                            title={product.active ? "Hide product" : "Show product"}
                          >
                            {product.active ? <Eye size={18} /> : <EyeSlash size={18} />}
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <a
                            href={product.amazon_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-[#6B6661] hover:text-[#0F766E]"
                            title="View on Amazon"
                          >
                            <ArrowSquareOut size={18} />
                          </a>
                          <button
                            onClick={() => openEditModal(product)}
                            className="p-2 text-[#6B6661] hover:text-[#0F766E]"
                            title="Edit"
                          >
                            <Pencil size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(product.id)}
                            className="p-2 text-[#6B6661] hover:text-[#DC2626]"
                            title="Delete"
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[#E0D4C8] p-4 flex items-center justify-between">
              <h2 className="font-serif text-xl">{editingProduct ? "Edit Product" : "Add Product"}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-[#6B6661] hover:text-[#33302E]">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Product Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Description *</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Price (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Original Price (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.original_price}
                    onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                    placeholder="For showing discounts"
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Image URL *</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                    required
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Amazon URL *</label>
                  <input
                    type="url"
                    value={formData.amazon_url}
                    onChange={(e) => setFormData({ ...formData, amazon_url: e.target.value })}
                    placeholder="https://www.amazon.co.uk/dp/XXXXX?tag=farmshop04-21"
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                    required
                  />
                  <p className="text-xs text-[#6B6661] mt-1">Include your affiliate tag: ?tag=farmshop04-21</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Brand</label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Rating (1-5)</label>
                  <input
                    type="number"
                    step="0.1"
                    min="1"
                    max="5"
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Review Count</label>
                  <input
                    type="number"
                    value={formData.review_count}
                    onChange={(e) => setFormData({ ...formData, review_count: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Gender</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                  >
                    <option value="unisex">Unisex</option>
                    <option value="male">Men</option>
                    <option value="female">Women</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#6B6661] mb-1">Tags</label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                    placeholder="boots, waterproof, safety (comma separated)"
                    className="w-full px-3 py-2 border border-[#E0D4C8] focus:outline-none focus:border-[#0F766E]"
                  />
                </div>

                <div className="col-span-2 flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.featured}
                      onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                      className="w-4 h-4 text-[#0F766E]"
                    />
                    <span className="text-sm">Featured Product</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="w-4 h-4 text-[#0F766E]"
                    />
                    <span className="text-sm">Active (visible in shop)</span>
                  </label>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F9FAFB]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <CircleNotch size={18} className="animate-spin" /> : <Check size={18} />}
                  {editingProduct ? "Update" : "Add"} Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-[#E0D4C8] p-4 flex items-center justify-between">
              <h2 className="font-serif text-xl">Bulk Import Products</h2>
              <button onClick={() => setShowBulkImport(false)} className="p-2 text-[#6B6661] hover:text-[#33302E]">
                <X size={20} />
              </button>
            </div>

            <div className="p-4">
              <p className="text-sm text-[#6B6661] mb-4">
                Paste a JSON array of products. Each product should have: title, description, price, image_url, amazon_url, category.
              </p>
              
              <textarea
                value={bulkJson}
                onChange={(e) => setBulkJson(e.target.value)}
                placeholder='[{"title": "Product Name", "description": "...", "price": 29.99, "image_url": "https://...", "amazon_url": "https://amazon.co.uk/dp/XXX?tag=farmshop04-21", "category": "tools", "brand": "Brand", "featured": true}]'
                rows={15}
                className="w-full px-3 py-2 border border-[#E0D4C8] font-mono text-sm focus:outline-none focus:border-[#0F766E]"
              />

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowBulkImport(false)}
                  className="flex-1 px-4 py-2 border border-[#E0D4C8] text-[#6B6661] hover:bg-[#F9FAFB]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkImport}
                  disabled={importing || !bulkJson.trim()}
                  className="flex-1 px-4 py-2 bg-[#0F766E] text-white hover:bg-[#0D6B64] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {importing ? <CircleNotch size={18} className="animate-spin" /> : <Upload size={18} />}
                  Import Products
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
